import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getSessions } from "@/lib/api";
import { AdminSideNav } from "@/pages/admin/AdminDashboardPage";
import BrandedHeader from "@/components/BrandedHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Brain, Eye, Loader2, Users, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function AdminSessionsPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => { loadSessions(); }, [statusFilter]);

  const loadSessions = async () => {
    try {
      const res = await getSessions(statusFilter || undefined);
      setSessions(res.data);
    } catch (e) {
      toast.error("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    active: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border border-[hsl(var(--success))]/25",
    completed: "bg-primary/15 text-primary border border-primary/25",
    ended: "bg-secondary text-muted-foreground",
  };

  return (
    <div className="min-h-screen bg-background flex">
      <AdminSideNav active="Sessions" />
      
      <div className="flex-1 flex flex-col">
        <BrandedHeader className="lg:hidden">
          <h1 className="text-lg font-display font-semibold text-center">Interview Sessions</h1>
        </BrandedHeader>

        <div className="px-4 sm:px-6 lg:px-8 py-6 flex-1">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-display font-semibold">Interview Sessions</h1>
                <p className="text-muted-foreground text-sm mt-1">Monitor and review all interview sessions</p>
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2 mb-6" data-testid="admin-sessions-status-filter">
              {["", "active", "completed"].map(status => (
                <Button
                  key={status}
                  variant={statusFilter === status ? "default" : "secondary"}
                  size="sm"
                  onClick={() => { setStatusFilter(status); setLoading(true); }}
                  className="btn-press"
                >
                  {status || "All"}
                </Button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 text-primary animate-spin" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-20">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No sessions found</p>
              </div>
            ) : (
              <div className="border border-border rounded-xl overflow-hidden" data-testid="admin-sessions-table">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-secondary/30">
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Candidate</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3 hidden sm:table-cell">Started</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Status</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3 hidden md:table-cell">Stage</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Proctor Issues</th>
                      <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map(s => {
                      const proctorIssues = (s.proctoring_summary?.tab_switches || 0) + 
                        (s.proctoring_summary?.face_not_visible || 0) + 
                        (s.proctoring_summary?.multiple_faces || 0);
                      return (
                        <tr key={s.id} className="border-b border-border last:border-0 hover:bg-secondary/20 transition-colors">
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium">{s.candidate_name}</p>
                            <p className="text-xs text-muted-foreground">{s.candidate_email}</p>
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            <span className="text-sm text-muted-foreground">
                              {s.started_at ? new Date(s.started_at).toLocaleString() : '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className={`text-xs ${statusColors[s.status] || ''}`}>
                              {s.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <span className="text-sm text-muted-foreground">
                              Stage {s.interview_state?.current_stage ?? '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <Badge variant="secondary" className={`text-xs ${
                              proctorIssues === 0 ? 'text-[hsl(var(--success))]' :
                              proctorIssues <= 2 ? 'text-[hsl(var(--warning))]' : 'text-[hsl(var(--destructive))]'
                            }`}>
                              {proctorIssues} events
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {s.status === 'completed' && (
                              <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/reports/${s.id}`)}>
                                <Eye className="w-3.5 h-3.5 mr-1" /> Report
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
